import { useEffect, useState } from 'react'
import { Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { Clock3 } from '@/components/ui/icons'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { GlassSheet } from '@/components/ui/GlassSheet'
import { useRespondToPaidRental } from '@/lib/hooks/use-booking'
import { useTheme } from '@/lib/hooks/use-theme'
import { useSemanticColors, SPACING } from '@/lib/theme/tokens'
import { FONTS } from '@/lib/brand'
import { formatBookingDateTime } from '@/lib/utils/dates'
import { captureError } from '@/lib/utils/observability'
import { toast } from '@/lib/toast'

type Props = {
  rentalId: string
  deadlineAt: string | null
  rentalTermsVersion: string | null
  isHost: boolean
  onResolved: () => Promise<unknown>
}

export function HostApprovalCard({
  rentalId,
  deadlineAt,
  rentalTermsVersion,
  isHost,
  onResolved,
}: Props) {
  const { t, i18n } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { respond, isPending } = useRespondToPaidRental()
  const [now, setNow] = useState(Date.now())
  const [showDecline, setShowDecline] = useState(false)

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 15_000)
    return () => clearInterval(timer)
  }, [])

  const deadlineMs = deadlineAt ? new Date(deadlineAt).getTime() : Number.NaN
  const expired = !Number.isFinite(deadlineMs) || now >= deadlineMs
  const remainingMinutes = expired ? 0 : Math.ceil((deadlineMs - now) / 60_000)
  const deadlineLabel = deadlineAt
    ? formatBookingDateTime(deadlineAt, i18n.language, {
        day: 'numeric',
        month: 'short',
        hour: 'numeric',
        minute: '2-digit',
      })
    : null

  const decide = async (decision: 'accept' | 'decline') => {
    try {
      await respond({ rentalId, decision })
      setShowDecline(false)
      await onResolved()
      toast.success(t(decision === 'accept' ? 'host_approval.accepted' : 'host_approval.declined'))
    } catch (error) {
      if ((error as { code?: string })?.code === 'HOST_DECISION_UNAVAILABLE') {
        toast.info(t('host_approval.expired'))
      } else {
        captureError(error, { component: 'HostApprovalCard', action: decision })
        toast.error(t('host_approval.error'))
      }
      await onResolved()
    }
  }

  return (
    <>
      <GlassSheet
        testID="host-approval.card"
        style={{ marginTop: SPACING.lg }}
        contentStyle={{ padding: SPACING.lg, gap: SPACING.sm }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.sm }}>
          <Clock3 size={21} color={colors.textAccent} />
          <Text
            style={{ flex: 1, color: colors.textPrimary, fontFamily: FONTS.bodyBold, fontSize: 17 }}
          >
            {t(isHost ? 'host_approval.host_title' : 'host_approval.waiting_title')}
          </Text>
        </View>
        <Text
          style={{
            color: colors.textSecondary,
            fontFamily: FONTS.body,
            fontSize: 14,
            lineHeight: 21,
          }}
        >
          {t(isHost ? 'host_approval.host_body' : 'host_approval.guest_body')}
        </Text>
        <Text
          testID="host-approval.deadline"
          accessibilityLiveRegion="polite"
          style={{
            color: expired ? colors.error : colors.textAccent,
            fontFamily: FONTS.bodyBold,
            fontSize: 14,
          }}
        >
          {expired
            ? t('host_approval.expired')
            : isHost
              ? t('host_approval.time_remaining', { minutes: remainingMinutes })
              : deadlineLabel
                ? t('host_approval.guest_deadline', { time: deadlineLabel })
                : t('host_approval.guest_body')}
        </Text>
        {isHost && !expired && (
          <View style={{ gap: SPACING.sm, marginTop: SPACING.xs }}>
            <Button
              title={t('host_approval.accept')}
              variant="primary"
              size="lg"
              loading={isPending}
              disabled={isPending}
              onPress={() => void decide('accept')}
              testID="host-approval.accept"
            />
            <Button
              title={t('host_approval.decline')}
              variant="secondary"
              size="lg"
              disabled={isPending}
              onPress={() => setShowDecline(true)}
              testID="host-approval.decline"
            />
          </View>
        )}
      </GlassSheet>
      <ConfirmDialog
        visible={showDecline}
        title={t('host_approval.decline_title')}
        message={t(
          rentalTermsVersion === '2.0'
            ? 'host_approval.decline_body_v2'
            : 'host_approval.decline_body',
        )}
        confirmLabel={t('host_approval.decline_confirm')}
        cancelLabel={t('host_approval.keep_request')}
        confirmLoading={isPending}
        onConfirm={() => void decide('decline')}
        onCancel={() => setShowDecline(false)}
        testIDPrefix="host-approval.decline-confirmation"
      />
    </>
  )
}
